import type { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import type { ValidatingHttpFetcherWithBaseUrl } from '../validating-http-fetcher';
import type { FeatureFlagService, FeatureSet } from './feature-flag-service';
import { featureSetSchema } from './feature-flag-service';

export type PrivacyProxyFeatureFlagServiceDependencies = {
    readonly queryClient: QueryClient;
    readonly validatingRealtimeApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

const flagsPathname = '/privacy-proxy/flags';

// The backend resolves every client-side flag server-side (driven by the JWT) and returns
// LaunchDarkly's per-flag envelope. We only consume `value`; the remaining fields are ignored.
const resolvedFlagsResponseSchema = z.record(z.string(), z.object({ value: z.unknown() }));

const toFeatureSet = (response: z.infer<typeof resolvedFlagsResponseSchema>): FeatureSet =>
    featureSetSchema.parse(Object.fromEntries(Object.entries(response).map(([key, flag]) => [key, flag.value])));

export const createPrivacyProxyFeatureFlagService = (
    dependencies: PrivacyProxyFeatureFlagServiceDependencies,
): FeatureFlagService => {
    const { queryClient, validatingRealtimeApiFetcher } = dependencies;

    // Every flag stays off until the snapshot resolves, mirroring LaunchDarkly's default-variation fallback.
    let featureSet: FeatureSet = featureSetSchema.parse({});

    let markedAsInitialized = (): void => {
        /* empty */
    };

    const initializedPromise = new Promise<void>((resolve) => {
        markedAsInitialized = resolve;
    });

    return {
        async initialize(userId) {
            try {
                const flags = await queryClient.fetchQuery({
                    queryFn: async () => {
                        const result = await validatingRealtimeApiFetcher.get(resolvedFlagsResponseSchema, {
                            pathname: flagsPathname,
                        });

                        if (result.isOk) {
                            return result.value;
                        }

                        throw result.error;
                    },
                    queryKey: ['feature-flags', userId],
                });

                featureSet = toFeatureSet(flags);
            } catch {
                // Endpoint unreachable or response invalid: keep every flag off (safe default), mirroring
                // LaunchDarkly's fallback variation. The validating fetcher reports schema mismatches and the
                // query client retries transient failures, so we just swallow here.
            } finally {
                // Always resolve so the app boots with flags off instead of hanging on the init gate.
                markedAsInitialized();
            }
        },
        isFeatureFlagEnabled(featureFlagName) {
            return featureSet[featureFlagName];
        },
        async isInitialized() {
            await initializedPromise;

            return true;
        },
    };
};
